"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { lazy, type PropsWithChildren, type ReactNode, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { handleBoundaryError } from "@/components/errors/handle-boundary-error";
import { MainErrorFallback } from "@/components/errors/main";
import { ReauthenticationRequired } from "@/components/errors/reauthentication-required";
import { useEvent } from "@/lib/hooks/use-event";
import { useDescopeLogout } from "@/lib/integrations/descope";
import { getQueryClient } from "@/lib/integrations/react-query";

const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then((d) => ({
    default: d.ReactQueryDevtools,
  })),
);

export function QueryProvider({ children }: PropsWithChildren) {
  const queryClient = getQueryClient();

  return (
    <ErrorBoundary FallbackComponent={MainErrorFallback} onError={handleBoundaryError}>
      <ReAuthErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {children}

          <ReactQueryDevtools initialIsOpen={false} />

          <DevtoolsOnProd />
        </QueryClientProvider>
      </ReAuthErrorBoundary>
    </ErrorBoundary>
  );
}

function DevtoolsOnProd() {
  const [showDevtools, setShowDevtools] = useState(false);

  useEffect(() => {
    // Extend the Window interface to include toggleDevtools
    (window as typeof window & { toggleDevtools?: () => void }).toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  if (showDevtools) {
    return <ReactQueryDevtoolsProduction />;
  }

  return null;
}

function ReAuthErrorBoundary({ children }: { children: ReactNode }) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { goToLogoutPage } = useDescopeLogout();

  function reauthRequired() {
    setIsRedirecting(true);

    setTimeout(() => {
      goToLogoutPage();
    }, 3000); // Delay to ensure overlay shows
  }

  useEvent("__ausd_reauth-required", reauthRequired);

  if (isRedirecting) {
    return <ReauthenticationRequired />;
  }

  return <>{children}</>;
}
